import React from 'react';
import { PERM, type Permission } from '../auth/permissions';
import WelcomeIcon from '@/components/icons/WelcomeIcon';
import CourseLibraryIcon from '@/components/icons/CourseLibraryIcon';
import LearningProgramsIcon from '@/components/icons/LearningProgramsIcon';
import AnalyticsIcon from '@/components/icons/AnalyticsIcon';
import GamificationIcon from '@/components/icons/GamificationIcon';
import LearnersIcon from '@/components/icons/LearnersIcon';
import PrizeManagementIcon from '@/components/icons/PrizeManagementIcon';
import VerifiedIcon from '@/components/icons/VerifiedIcon';
import ChatIcon from '@/components/icons/ChatIcon';
import CalendarIcon from '@/components/icons/CalendarIcon';
import {
  FEATURE,
  normalizeTierId,
  planHasFeature,
  tiersWithFeature,
  tiersWithAllowance,
} from '@/components/settings/tiers/tierCatalog';

export interface NavItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  /** When set, the item only renders if the active membership holds this perm. */
  perm?: Permission;
  /**
   * When set, the item only renders if the tenant's subscription plan is in
   * this list (feature-tier gating, on top of any `perm` check). Mirrors the
   * backend's plan gates so the nav never links to a tier-locked feature.
   */
  tiers?: string[];
}

/**
 * Plans on which the gamification + prize features are available. Mirrors the
 * backend `GAMIFICATION_PLANS` (`app/services/gamification_service.py`).
 *
 * Note: these features are intentionally NOT used to hide their nav entries —
 * Gamification and Prize Management stay visible on every plan so members can
 * discover them; the pages themselves show a `FeatureUpgradeNotice` when the
 * tenant's plan doesn't include the feature. Use `planSupportsGamification`.
 */
export const GAMIFICATION_TIERS: string[] = tiersWithFeature(FEATURE.prizeShop);

/** Plans with the leaderboard side of gamification (points, rankings,
 *  resets) — Growth is "leaderboard only" per the tier offer. Mirrors the
 *  backend's LEADERBOARD_PLANS. */
export const LEADERBOARD_TIERS: string[] = tiersWithFeature(FEATURE.leaderboard);

/** Whether the tenant's plan unlocks FULL gamification (incl. prize shop). */
export const planSupportsGamification = (plan: string | null | undefined): boolean =>
  plan != null && planHasFeature(FEATURE.prizeShop, plan);

/** Whether the tenant's plan unlocks the leaderboard (Growth and up). */
export const planSupportsLeaderboard = (plan: string | null | undefined): boolean =>
  plan != null && planHasFeature(FEATURE.leaderboard, plan);

/** Plans with the calendar (+ Google integration) — Growth and up. */
export const CALENDAR_TIERS: string[] = tiersWithFeature(FEATURE.calendar);

/** Plans that can have an active learning program (Free's allowance is 0, so
 *  the whole Programs surface is unreachable there — Aleix's ripple rule 9/8). */
export const PROGRAM_TIERS: string[] = tiersWithAllowance('publishedPrograms');

/**
 * Single source of truth for the sidebar nav, shared by `AppLayout` (rendering)
 * and `ViewModeContext` (deriving `canManage` + the management landing path).
 */

// Universal learning surface — shown to every authenticated member.
export const LEARNING: NavItem[] = [
  { name: 'Home', path: '/learn/dashboard', icon: <WelcomeIcon className="w-6 h-6" /> },
  { name: 'My Courses', path: '/learn/courses', icon: <CourseLibraryIcon className="w-6 h-6" /> },
  { name: 'Programs', path: '/learn/programs', icon: <LearningProgramsIcon className="w-6 h-6" />, tiers: PROGRAM_TIERS },
  // Hidden unless the plan includes the prize shop (product rule 2026-07-24:
  // plan/upsell talk is for admins & trainers — learners never see a locked
  // feature or upgrade copy).
  { name: 'Points & Prizes', path: '/learn/prizes', icon: <GamificationIcon className="w-6 h-6" />, tiers: GAMIFICATION_TIERS },
  { name: 'Certificates', path: '/learn/certificates', icon: <VerifiedIcon className="w-6 h-6" /> },
];

// Management surface — each item is gated by permission. `Dashboard` leads the
// list so analytics-capable staff land there; members without `analytics.view`
// fall through to the first item they can access (e.g. Course Library).
// Settings is intentionally NOT here — it lives as a gear icon in the header.
export const MANAGE: NavItem[] = [
  { name: 'Dashboard', path: '/dashboard', icon: <WelcomeIcon className="w-6 h-6" />, perm: PERM.ANALYTICS_VIEW },
  { name: 'Course Library', path: '/courses', icon: <CourseLibraryIcon className="w-6 h-6" />, perm: PERM.COURSE_AUTHOR },
  { name: 'Learning Programs', path: '/programs', icon: <LearningProgramsIcon className="w-6 h-6" />, perm: PERM.PROGRAM_MANAGE, tiers: PROGRAM_TIERS },
  { name: 'Analytics', path: '/analytics', icon: <AnalyticsIcon className="w-6 h-6" />, perm: PERM.ANALYTICS_VIEW },
  { name: 'Gamification', path: '/gamification', icon: <GamificationIcon className="w-6 h-6" />, perm: PERM.GAMIFICATION_MANAGE },
  { name: 'Prize Management', path: '/prizes', icon: <PrizeManagementIcon className="w-6 h-6" />, perm: PERM.PRIZE_MANAGE },
  { name: 'Learners', path: '/learners', icon: <LearnersIcon className="w-6 h-6" />, perm: PERM.MEMBER_MANAGE },
];

// Universal utilities — available to every member (no permission required).
// Rendered as a shared sidebar section beneath the active mode's group, with
// the path pointing at the surface that matches the current mode so following
// the link never flips view mode unexpectedly. Deliberately NOT part of MANAGE:
// these carry no `perm`, so including them there would make every member look
// like staff (`canManage`).
export const SHARED_LEARN: NavItem[] = [
  { name: 'Chat', path: '/learn/chat', icon: <ChatIcon className="w-6 h-6" /> },
  { name: 'Calendar', path: '/learn/calendar', icon: <CalendarIcon className="w-6 h-6" />, tiers: CALENDAR_TIERS },
];

export const SHARED_MANAGE: NavItem[] = [
  { name: 'Chat', path: '/chat', icon: <ChatIcon className="w-6 h-6" /> },
  { name: 'Calendar', path: '/calendar', icon: <CalendarIcon className="w-6 h-6" />, tiers: CALENDAR_TIERS },
];

/**
 * Whether a nav item should render for the current member: it must hold the
 * item's `perm` (if any) AND the tenant's `plan` must be in the item's `tiers`
 * (if any). Both gates are independent — an item can require a permission, a
 * tier, both, or neither.
 */
export const isNavItemVisible = (
  item: NavItem,
  can: (perm: Permission) => boolean,
  plan: string | null | undefined
): boolean =>
  (!item.perm || can(item.perm)) &&
  (!item.tiers || (plan != null && item.tiers.includes(normalizeTierId(plan))));
